import { captureMessage } from '@sentry/react';
import { useSanityDocumentsMap } from 'modules/cms/useSanityDocuments';
import { useMemo } from 'react';
import { useSoftwareProducts } from './useSoftwareProducts';

/**
 * Maps software to CMS software product.
 * Only returns mapped data, orther SWR values are only for SoftwareProducts, like `mutate`.
 */
export const useCmsMappedSoftwareProducts = () => {
  // Since loaded in Main, this is most probably already loaded here
  // (why I dont care about the loading state)
  const cmsSoftwareMap = useSanityDocumentsMap(['softwareProduct']).data?.softwareProduct;
  const { data: softwareProducts, ...rest } = useSoftwareProducts();

  const mappedSoftwareProducts = useMemo(() => {
    if (!softwareProducts || !cmsSoftwareMap) return;

    // Map software products to their CMS counterparts
    const mappedProducts = softwareProducts
      .map((product) => {
        const cmsProduct = cmsSoftwareMap.get(product.commercialProductId);
        if (!cmsProduct) {
          console.warn(`No CMS product found for commercialProductId: ${product.commercialProductId}`);
          captureMessage(`No CMS product found for commercialProductId: ${product.commercialProductId}`, {
            level: 'warning',
            fingerprint: ['unmatched-cms-product'],
          });
          return null;
        }
        return {
          icxProduct: product,
          cmsData: cmsProduct,
        };
      })
      .filter((x) => x !== null);

    return mappedProducts;
  }, [softwareProducts, cmsSoftwareMap]);

  return {
    mappedSoftwareProducts,
    ...rest,
  };
};

export type MappedSoftwareProduct = NonNullable<
  ReturnType<typeof useCmsMappedSoftwareProducts>['mappedSoftwareProducts']
>[number];
